import type {
  WorkJourneyItemRow,
  WorkJourneyRow,
  WorkResponsibilityRow,
  WorkPolicySnapshotRow
} from '../api';
import type { WorkAttentionLevel, WorkStageState } from './work-radar';

export type JourneyStageGroup = {
  key: string;
  label: string;
  state: WorkStageState;
  count: number;
  detail: string;
  items: WorkJourneyItemRow[];
};

export type JourneyRadarItem = {
  journey: WorkJourneyRow;
  attentionLevel: WorkAttentionLevel;
  attentionLabel: string;
  attentionDetail: string;
  ageDays: number;
  isStale: boolean;
  hasFailedSignal: boolean;
  hasLinkGap: boolean;
  linkGapDetail?: string;
  stages: JourneyStageGroup[];
  nextAction?: JourneyOperationalAction;
  searchText: string;
};

export type JourneyOperationalAction = {
  id: string;
  journeyId: string;
  expectationId?: string;
  stageKey?: string;
  stageLabel: string;
  title: string;
  detail: string;
  category:
    | 'human_action'
    | 'failure'
    | 'missing_evidence'
    | 'stale'
    | 'external_wait'
    | 'divergence';
  waitingOn: 'human' | 'system' | 'external' | 'unknown';
  severity: 'info' | 'warning' | 'critical';
  firstDetectedAt: string;
  target: string;
  url?: string;
  assignedToUserId?: string;
  assignedToEmail?: string;
  acknowledgedAt?: string;
  cause?: string;
  evidenceItemId?: string;
  remediation?: 'rerun_failed_jobs';
  responsibilities: WorkResponsibilityRow[];
  policy?: WorkPolicySnapshotRow;
  requiredOwners: string[];
  matchedPaths: string[];
};

function actionResponsibilities(
  journey: WorkJourneyRow,
  expectation?: WorkJourneyRow['expectations'][number]
) {
  const directlyObserved = new Set(expectation?.observedItemIds || []);
  const prerequisiteObserved = new Set(
    journey.expectations
      .filter((candidate) =>
        expectation?.dependsOn.includes(candidate.stageKey)
      )
      .flatMap((candidate) => candidate.observedItemIds)
  );
  const ranked = [...(journey.responsibilities || [])].sort((left, right) => {
    const relevance = (value: WorkResponsibilityRow) =>
      directlyObserved.has(value.workJourneyItemId || '')
        ? 0
        : prerequisiteObserved.has(value.workJourneyItemId || '')
          ? 1
          : 2;
    const role = { reviewer: 0, approver: 0, assignee: 1, owner: 1, author: 2 };
    return (
      relevance(left) - relevance(right) || role[left.role] - role[right.role]
    );
  });
  const seen = new Set<string>();
  return ranked
    .filter((responsibility) => {
      const key =
        responsibility.identity?.workIdentityId ||
        `team:${responsibility.teamHandle}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 4);
}

const DAY_MS = 24 * 60 * 60 * 1000;

const STAGE_DEFINITIONS: Array<{
  key: string;
  label: string;
  kinds: WorkJourneyItemRow['kind'][];
}> = [
  { key: 'task', label: 'Tasks', kinds: ['task'] },
  { key: 'code', label: 'Code', kinds: ['pull_request'] },
  { key: 'verify', label: 'Verify', kinds: ['check', 'workflow'] },
  { key: 'build', label: 'Build', kinds: ['build'] },
  {
    key: 'deploy',
    label: 'Deploy',
    kinds: ['deployment', 'infrastructure']
  },
  {
    key: 'release',
    label: 'Release',
    kinds: ['distribution', 'release']
  }
];

function daysSince(value: string, now: Date) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 0;
  return Math.max(0, Math.floor((now.getTime() - parsed.getTime()) / DAY_MS));
}

function aggregateState(items: WorkJourneyItemRow[]): WorkStageState {
  if (items.some((item) => item.status === 'failed')) return 'failed';
  if (items.some((item) => item.status === 'blocked')) return 'blocked';
  if (items.some((item) => item.status === 'running')) return 'running';
  if (items.some((item) => item.status === 'pending')) return 'pending';
  if (items.some((item) => item.status === 'unknown')) return 'unknown';
  if (
    items.length > 0 &&
    items.every((item) => ['success', 'closed'].includes(item.status))
  ) {
    return items.some((item) => item.status === 'success')
      ? 'success'
      : 'skipped';
  }
  return 'unknown';
}

function expectationState(
  status: WorkJourneyRow['expectations'][number]['status']
): WorkStageState {
  return {
    pending: 'pending',
    waiting: 'unknown',
    running: 'running',
    blocked: 'blocked',
    failed: 'failed',
    complete: 'success',
    not_applicable: 'skipped'
  }[status] as WorkStageState;
}

const EXPECTATION_PRIORITY = {
  failed: 0,
  blocked: 1,
  running: 2,
  waiting: 3,
  pending: 4,
  complete: 5,
  not_applicable: 6
};

export function journeyFocusExpectation(journey: WorkJourneyRow) {
  const openFindings = (journey.attention || [])
    .filter((finding) => finding.status === 'open')
    .sort((left, right) => {
      const severity = { critical: 0, warning: 1, info: 2 };
      return severity[left.severity] - severity[right.severity];
    });
  const findingExpectation = openFindings
    .map((finding) =>
      journey.expectations.find(
        (expectation) =>
          expectation.workJourneyExpectationId ===
          finding.workJourneyExpectationId
      )
    )
    .find(Boolean);
  if (findingExpectation) return findingExpectation;

  const complete = new Set(
    journey.expectations
      .filter((expectation) =>
        ['complete', 'not_applicable'].includes(expectation.status)
      )
      .map((expectation) => expectation.stageKey)
  );
  const reachable = journey.expectations.filter(
    (expectation) =>
      !['complete', 'not_applicable'].includes(expectation.status) &&
      expectation.dependsOn.every((dependency) => complete.has(dependency))
  );
  return [...(reachable.length ? reachable : journey.expectations)]
    .filter(
      (expectation) =>
        !['complete', 'not_applicable'].includes(expectation.status)
    )
    .sort(
      (left, right) =>
        EXPECTATION_PRIORITY[left.status] - EXPECTATION_PRIORITY[right.status]
    )[0];
}

export function journeyOperationalAction(
  journey: WorkJourneyRow
): JourneyOperationalAction | undefined {
  const finding = [...(journey.attention || [])]
    .filter(
      (candidate) =>
        candidate.status === 'open' &&
        (!candidate.metadata?.snoozedUntil ||
          new Date(candidate.metadata.snoozedUntil).getTime() <= Date.now())
    )
    .sort((left, right) => {
      const severity = { critical: 0, warning: 1, info: 2 };
      const category = {
        human_action: 0,
        failure: 1,
        missing_evidence: 2,
        divergence: 3,
        stale: 4,
        external_wait: 5
      };
      return (
        severity[left.severity] - severity[right.severity] ||
        category[left.category] - category[right.category]
      );
    })[0];
  if (!finding) return undefined;

  const policy = journey.policies?.find(
    (candidate) =>
      candidate.workPolicySnapshotId === finding.metadata?.['policySnapshotId']
  );
  const requiredOwners = policy?.requiresCodeOwnerReview
    ? [
        ...new Set(
          policy.requirements.flatMap((requirement) =>
            requirement.owners.map((value) => `@${value.handle}`)
          )
        )
      ]
    : [];
  const matchedPaths = policy?.requiresCodeOwnerReview
    ? [
        ...new Set(
          policy.requirements.flatMap((requirement) => requirement.matchedPaths)
        )
      ]
    : [];

  const expectation = journey.expectations.find(
    (candidate) =>
      candidate.workJourneyExpectationId === finding.workJourneyExpectationId
  );
  const policyEvidenceItemId =
    typeof finding.metadata?.['evidenceItemId'] === 'string'
      ? finding.metadata['evidenceItemId']
      : undefined;
  const observedSignal =
    journey.items.find((item) =>
      expectation?.observedItemIds.includes(item.workJourneyItemId)
    ) ||
    journey.items.find(
      (item) => item.workJourneyItemId === policyEvidenceItemId
    );
  const prerequisiteItemIds = new Set(
    journey.expectations
      .filter((candidate) =>
        expectation?.dependsOn.includes(candidate.stageKey)
      )
      .flatMap((candidate) => candidate.observedItemIds)
  );
  const prerequisiteSignal = [...journey.items]
    .filter(
      (item) => prerequisiteItemIds.has(item.workJourneyItemId) && item.url
    )
    .sort((left, right) =>
      (
        right.updated ||
        right.finishedAt ||
        right.startedAt ||
        right.created ||
        ''
      ).localeCompare(
        left.updated || left.finishedAt || left.startedAt || left.created || ''
      )
    )[0];
  const sourceSignal =
    observedSignal ||
    prerequisiteSignal ||
    [...journey.items]
      .filter((item) => item.url)
      .sort((left, right) =>
        (
          right.updated ||
          right.finishedAt ||
          right.startedAt ||
          right.created ||
          ''
        ).localeCompare(
          left.updated ||
            left.finishedAt ||
            left.startedAt ||
            left.created ||
            ''
        )
      )[0];
  const ref =
    sourceSignal?.externalRefType === 'pull_request' &&
    sourceSignal.externalRefId
      ? ` · PR #${sourceSignal.externalRefId}`
      : '';

  return {
    id: finding.workJourneyAttentionId,
    journeyId: journey.workJourneyId,
    expectationId: expectation?.workJourneyExpectationId,
    stageKey: expectation?.stageKey,
    stageLabel: expectation?.label || finding.title,
    title: finding.title,
    detail: finding.detail,
    category: finding.category,
    waitingOn: finding.waitingOn,
    severity: finding.severity,
    firstDetectedAt: finding.firstDetectedAt,
    target: sourceSignal?.externalRepo
      ? `${sourceSignal.externalRepo}${ref}`
      : expectation?.label || 'Journey evidence',
    url: sourceSignal?.url,
    assignedToUserId: finding.metadata?.assignedToUserId,
    assignedToEmail: finding.metadata?.assignedToEmail,
    acknowledgedAt: finding.metadata?.acknowledgedAt,
    cause:
      observedSignal?.status === 'failed'
        ? observedSignal.externalRefType === 'workflow_run'
          ? String(
              observedSignal.metadata?.['causeSummary'] ||
                `GitHub workflow ${observedSignal.title} ${String(observedSignal.metadata?.['failureKind'] || 'failed')}`
            )
          : `${observedSignal.title} reported ${observedSignal.status}`
        : undefined,
    evidenceItemId: observedSignal?.workJourneyItemId,
    remediation:
      observedSignal?.provider === 'github' &&
      observedSignal.externalRefType === 'workflow_run' &&
      observedSignal.status === 'failed'
        ? 'rerun_failed_jobs'
        : undefined,
    responsibilities: actionResponsibilities(journey, expectation),
    policy,
    requiredOwners,
    matchedPaths
  };
}

function groupDetail(
  label: string,
  items: WorkJourneyItemRow[],
  taskCount: number
) {
  const count =
    label === 'Tasks' ? Math.max(taskCount, items.length) : items.length;
  if (count === 0) return `No ${label.toLowerCase()} signal connected`;
  if (label === 'Tasks') {
    return count === 1
      ? '1 task reference linked'
      : `${count} task references converge in this journey`;
  }
  if (count === 1) return items[0]?.title || `1 ${label.toLowerCase()} item`;
  return `${count} ${label.toLowerCase()} items converge in this journey`;
}

export function buildJourneyRadarItem(
  journey: WorkJourneyRow,
  now = new Date()
): JourneyRadarItem {
  const taskCount = Math.max(
    journey.taskRefs.length,
    journey.items.filter((item) => item.kind === 'task').length
  );
  const inferredStages = STAGE_DEFINITIONS.map((definition) => {
    const items = journey.items.filter((item) =>
      definition.kinds.includes(item.kind)
    );
    const virtualTaskCount =
      definition.key === 'task' ? taskCount : items.length;
    return {
      key: definition.key,
      label: definition.label,
      state:
        definition.key === 'task' && taskCount > 0
          ? 'success'
          : aggregateState(items),
      count: virtualTaskCount,
      detail: groupDetail(definition.label, items, taskCount),
      items
    } satisfies JourneyStageGroup;
  });
  const stages = journey.expectations?.length
    ? journey.expectations.map((expectation) => {
        const items = journey.items.filter((item) =>
          expectation.observedItemIds.includes(item.workJourneyItemId)
        );
        return {
          key: expectation.stageKey,
          label: expectation.label,
          state: expectationState(expectation.status),
          count: items.length || (expectation.kind === 'task' ? taskCount : 0),
          detail: expectation.detail || `Waiting on ${expectation.waitingOn}`,
          items
        } satisfies JourneyStageGroup;
      })
    : inferredStages;

  const ageDays = daysSince(journey.firstSeenAt, now);
  const runningPullRequests = journey.items.filter(
    (item) => item.kind === 'pull_request' && item.status === 'running'
  );
  const isStale = runningPullRequests.length > 0 && ageDays >= 14;
  const failedItems = journey.items.filter((item) => item.status === 'failed');
  const blockedItems = journey.items.filter(
    (item) => item.status === 'blocked'
  );
  const verificationItems = journey.items.filter((item) =>
    ['check', 'workflow'].includes(item.kind)
  );
  const downstreamItems = journey.items.filter((item) =>
    [
      'build',
      'deployment',
      'infrastructure',
      'distribution',
      'release'
    ].includes(item.kind)
  );
  const mergedSource = journey.items.some(
    (item) => item.kind === 'pull_request' && item.status === 'success'
  );
  const taskItems = journey.items.filter((item) => item.kind === 'task');
  const hasTaskContext = taskCount > 0;
  const hasCode = journey.items.some((item) => item.kind === 'pull_request');
  const hasDelivery = downstreamItems.length > 0;
  const taskLifecycleStates = taskItems.map((item) =>
    String(item.metadata?.['lifecycleState'] || '')
  );
  const taskNeedsCode =
    hasTaskContext &&
    !hasCode &&
    !hasDelivery &&
    ageDays >= 3 &&
    taskLifecycleStates.some((state) =>
      ['active', 'blocked', 'validation', 'delivered'].includes(state)
    );
  const codeNeedsTask = hasCode && !hasTaskContext;
  const taskClaimsDelivered =
    taskLifecycleStates.includes('delivered') && !hasDelivery;
  const deliveryOutranTask =
    hasDelivery &&
    taskItems.length > 0 &&
    journey.status === 'complete' &&
    !taskLifecycleStates.includes('delivered');
  const hasLinkGap =
    taskNeedsCode || codeNeedsTask || taskClaimsDelivered || deliveryOutranTask;
  const linkGapDetail = taskClaimsDelivered
    ? 'ClickUp says delivered, but no downstream delivery evidence is connected'
    : deliveryOutranTask
      ? 'Delivery completed, but the connected ClickUp task has not reached Delivered'
      : codeNeedsTask
        ? 'Code activity has no connected task intent'
        : taskNeedsCode
          ? 'Active task intent has no connected branch or pull request'
          : undefined;

  let attentionLevel: WorkAttentionLevel = 'active';
  let attentionLabel = 'Progressing';
  let attentionDetail = 'Signals are moving through the delivery journey';

  const topFinding = [...(journey.attention || [])].sort((left, right) => {
    const rank = { critical: 0, warning: 1, info: 2 };
    return rank[left.severity] - rank[right.severity];
  })[0];

  if (journey.status === 'closed') {
    attentionLevel = 'closed';
    attentionLabel = 'Closed';
    attentionDetail = 'No active work remains in this journey';
  } else if (topFinding) {
    attentionLevel =
      topFinding.severity === 'critical' ? 'critical' : 'attention';
    attentionLabel = topFinding.title;
    attentionDetail = topFinding.detail;
  } else if (failedItems.length > 0) {
    attentionLevel = 'critical';
    attentionLabel = 'Failure';
    attentionDetail = `${failedItems.length} failing signal${failedItems.length === 1 ? '' : 's'} need attention`;
  } else if (blockedItems.length > 0) {
    attentionLevel = 'critical';
    attentionLabel = 'Blocked';
    attentionDetail = `${blockedItems.length} stage${blockedItems.length === 1 ? '' : 's'} waiting on an explicit dependency`;
  } else if (isStale) {
    attentionLevel = 'critical';
    attentionLabel = 'Stale work';
    attentionDetail = `${runningPullRequests.length} open change${runningPullRequests.length === 1 ? '' : 's'} in a ${ageDays}-day journey`;
  } else if (mergedSource && downstreamItems.length === 0) {
    attentionLevel = 'attention';
    attentionLabel = 'Delivery unknown';
    attentionDetail =
      'Code merged, but no build, deployment, infrastructure, or release evidence is connected';
  } else if (hasLinkGap) {
    attentionLevel = 'attention';
    attentionLabel = 'Unlinked work';
    attentionDetail =
      linkGapDetail || 'Task and delivery evidence are not connected';
  } else if (
    runningPullRequests.length > 0 &&
    verificationItems.length === 0 &&
    ageDays >= 1
  ) {
    attentionLevel = 'attention';
    attentionLabel = 'Verification missing';
    attentionDetail = 'Open code has no connected check or workflow signal';
  } else if (
    journey.items.length > 0 &&
    journey.items.every((item) =>
      ['success', 'closed'].includes(item.status)
    ) &&
    downstreamItems.length > 0
  ) {
    attentionLevel = 'complete';
    attentionLabel = 'Delivered';
    attentionDetail = 'The connected journey has completed downstream evidence';
  }

  const persistedAction = journeyOperationalAction(journey);
  const linkGapAction: JourneyOperationalAction | undefined = hasLinkGap
    ? {
        id: `coverage:${journey.workJourneyId}`,
        journeyId: journey.workJourneyId,
        stageLabel: 'Evidence link',
        title: 'Connect missing evidence',
        detail: linkGapDetail || 'Task and delivery evidence are not connected',
        category: 'divergence',
        waitingOn: 'human',
        severity: 'warning',
        firstDetectedAt: journey.firstSeenAt,
        target: hasTaskContext ? 'ClickUp → GitHub' : 'GitHub → task intent',
        responsibilities: actionResponsibilities(journey),
        requiredOwners: [],
        matchedPaths: []
      }
    : undefined;

  return {
    journey,
    attentionLevel,
    attentionLabel,
    attentionDetail,
    ageDays,
    isStale,
    hasFailedSignal: failedItems.length > 0,
    hasLinkGap,
    linkGapDetail,
    stages,
    nextAction: persistedAction || linkGapAction,
    searchText: [
      journey.title,
      journey.taskRefs.join(' '),
      ...journey.items.flatMap((item) => [
        item.title,
        item.externalRepo,
        item.branch,
        item.author
      ]),
      ...(journey.responsibilities || []).flatMap((responsibility) => [
        responsibility.teamHandle,
        responsibility.identity?.displayName,
        responsibility.identity?.handle
      ])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
  };
}
